import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  MapPin, Clock, PartyPopper, Phone, IceCreamCone, Cake, BookOpen, Camera,
  ChevronDown, Heart, Sparkles, Star, ArrowRight, TreeDeciduous, Check,
} from "lucide-react";
import { SITE, IMAGES, GALLERY } from "../data/site";
import { Reveal, SectionHeading, CallButton, PartyButton, Confetti } from "../components/Shared";

const heroLines = [
  { text: "Come for the", color: "text-slate-900" },
  { text: "Snowballs.", color: "text-slate-900" },
  { text: "Stay for the", color: "text-blue-600" },
  { text: "Memories.", color: "text-blue-600" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const imageRotate = useTransform(scrollYProgress, [0, 1], [0, 2.5]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-blue-50 px-4 pb-20 pt-12 sm:px-6 sm:pt-16" data-testid="hero-section">
      <Confetti />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
        <div>
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-5 inline-block rounded-full bg-white px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-blue-700 shadow-sm"
          >
            A family-friendly snowball stand
          </motion.span>
          <h1 className="font-display text-5xl font-semibold leading-[1.04] sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            {heroLines.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.color}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.85, delay: 0.15 + i * 0.11, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
          >
            <p className="mt-5 text-base font-extrabold text-slate-800 md:text-lg" data-testid="hero-subheadline">
              A little sweetness, a little imagination, and a whole lot of memories.
            </p>
            <p className="mt-3 max-w-xl text-sm text-slate-600 sm:text-base">
              Ms. Jeanette's Shaved Ice Snowballs is a fun, family-friendly place for snowballs, treats,
              birthday celebrations, reading, pictures, and unforgettable family moments.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <PartyButton testid="hero-plan-party-btn" />
              <CallButton testid="hero-call-btn">{`Call Ms. Jeanette — ${SITE.phone}`}</CallButton>
            </div>
          </motion.div>
        </div>
        <div className="relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden rounded-3xl shadow-2xl shadow-blue-500/20"
          >
            <motion.img
              src={IMAGES.stand.url}
              alt={IMAGES.stand.alt}
              data-testid="hero-image"
              style={{ y: imageY, rotate: imageRotate, scale: 1.12 }}
              className="aspect-[4/3] w-full object-cover"
              loading="eager"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            className="animate-floaty absolute -left-3 top-6 rounded-2xl bg-white px-4 py-3 shadow-xl shadow-blue-500/20 sm:-left-6"
          >
            <div className="flex items-center gap-2">
              <TreeDeciduous className="h-5 w-5 text-green-600" aria-hidden="true" />
              <span className="text-xs font-extrabold text-slate-800 sm:text-sm">Reading Tree w/ Rewards</span>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.25 }}
            className="animate-floaty absolute -right-3 bottom-6 rounded-2xl bg-yellow-400 px-4 py-3 shadow-xl shadow-yellow-500/30 sm:-right-6"
            style={{ animationDelay: "1.2s" }}
          >
            <div className="flex items-center gap-2">
              <Cake className="h-5 w-5 text-slate-900" aria-hidden="true" />
              <span className="text-xs font-extrabold text-slate-900 sm:text-sm">Birthday Parties — $225</span>
            </div>
          </motion.div>
        </div>
      </div>
      <div className="relative mt-14 flex justify-center">
        <div className="flex flex-col items-center gap-1 text-blue-500" aria-hidden="true">
          <span className="text-xs font-extrabold uppercase tracking-widest">Scroll to explore</span>
          <ChevronDown className="animate-bounce-soft h-6 w-6" />
        </div>
      </div>
    </section>
  );
};

const marqueeWords = ["Snowballs", "Reading Tree", "Birthday Parties", "Sweet Treats", "Family Memories", "Stuffed Specialties"];

const Marquee = () => (
  <div className="overflow-hidden border-y-4 border-yellow-400 bg-blue-600 py-4" data-testid="marquee-strip" aria-hidden="true">
    <div className="animate-marquee flex w-max items-center">
      {[...marqueeWords, ...marqueeWords].map((word, i) => (
        <span key={`${word}-${i}`} className="flex items-center font-display text-xl font-semibold text-white sm:text-2xl">
          <span className="px-6">{word}</span>
          <Star className="h-5 w-5 shrink-0 text-yellow-300" fill="currentColor" />
        </span>
      ))}
    </div>
  </div>
);

const infoCards = [
  {
    icon: MapPin, title: "Visit Us", testid: "info-visit",
    lines: [SITE.address], bg: "bg-blue-100", color: "text-blue-600",
  },
  {
    icon: Clock, title: "Hours", testid: "info-hours",
    lines: ["Fri–Sun: 2:30–5:30 PM", "Mon–Thu: Closed"], bg: "bg-yellow-100", color: "text-yellow-600",
  },
  {
    icon: PartyPopper, title: "Birthday Parties", testid: "info-parties",
    lines: ["Available by booking with Ms. Jeanette"], bg: "bg-red-100", color: "text-red-500",
  },
  {
    icon: Phone, title: "Call", testid: "info-call",
    lines: [SITE.phone], href: SITE.phoneHref, bg: "bg-blue-100", color: "text-blue-600",
  },
];

const QuickInfo = () => (
  <section className="mx-auto mt-10 max-w-7xl px-4 sm:px-6" data-testid="quick-info-strip">
    <div className="grid gap-4 rounded-3xl border border-blue-100 bg-white p-5 shadow-xl shadow-blue-500/10 sm:grid-cols-2 sm:p-6 lg:grid-cols-4">
      {infoCards.map((card) => {
        const Icon = card.icon;
        const body = (
          <>
            <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${card.bg}`}>
              <Icon className={`h-6 w-6 ${card.color}`} aria-hidden="true" />
            </span>
            <span>
              <span className="block font-display text-base font-semibold text-slate-900">{card.title}</span>
              {card.lines.map((line) => (
                <span key={line} className="block text-sm font-semibold text-slate-600">{line}</span>
              ))}
            </span>
          </>
        );
        return card.href ? (
          <a key={card.title} href={card.href} data-testid={card.testid} className="flex items-start gap-3 rounded-2xl p-2 transition-colors hover:bg-blue-50 focus:outline-none focus:ring-4 focus:ring-blue-300">
            {body}
          </a>
        ) : (
          <div key={card.title} data-testid={card.testid} className="flex items-start gap-3 p-2">{body}</div>
        );
      })}
    </div>
  </section>
);

const chapters = [
  { num: "01", icon: IceCreamCone, title: "Snowballs", text: "Delicious shaved ice and specialty snowballs.", accent: "text-blue-600", border: "border-blue-500", testid: "more-card-snowballs" },
  { num: "02", icon: Cake, title: "Birthday Parties", text: "A unique place for children to celebrate.", accent: "text-red-500", border: "border-red-400", testid: "more-card-birthday" },
  { num: "03", icon: BookOpen, title: "Reading Tree", text: "A whimsical space where children can slow down, explore, read, and discover.", accent: "text-green-600", border: "border-green-500", testid: "more-card-reading" },
  { num: "04", icon: Camera, title: "Memories", text: "Pictures, family time, celebrations, and moments children will remember.", accent: "text-yellow-500", border: "border-yellow-400", testid: "more-card-memories" },
];

const MoreThan = () => (
  <section className="px-4 py-20 sm:px-6 sm:py-24" data-testid="more-than-snowball-section">
    <SectionHeading
      eyebrow="The Ms. Jeanette's experience"
      title="More Than a Snowball"
      sub="At Ms. Jeanette's, a visit can become a whole afternoon of fun. Grab a snowball, explore the Reading Tree, discover little doors and whimsical decorations, take pictures, celebrate a birthday, and spend time making memories with the people you love."
      testid="more-than-heading"
    />
    <div className="mx-auto grid max-w-6xl gap-6 sm:grid-cols-2">
      {chapters.map((chapter, i) => {
        const Icon = chapter.icon;
        return (
          <Reveal key={chapter.num} delay={i * 0.08}>
            <motion.div
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              data-testid={chapter.testid}
              className={`relative h-full overflow-hidden rounded-3xl border-t-8 bg-white p-8 shadow-lg shadow-blue-500/10 ${chapter.border}`}
            >
              <span className="pointer-events-none absolute -right-2 -top-7 select-none font-display text-8xl font-bold text-slate-100" aria-hidden="true">
                {chapter.num}
              </span>
              <span className="font-display text-sm font-semibold uppercase tracking-widest text-slate-400">Chapter {chapter.num}</span>
              <h3 className={`mt-2 flex items-center gap-3 font-display text-2xl font-semibold ${chapter.accent}`}>
                <Icon className="h-6 w-6" aria-hidden="true" /> {chapter.title}
              </h3>
              <p className="mt-3 text-sm font-semibold text-slate-600 sm:text-base">{chapter.text}</p>
            </motion.div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const treatsList = ["Snowballs", "Stuffed specialty snowballs", "Treats & sweets", "Drive-through service", "Outdoor seating"];

const TreatsTeaser = () => (
  <section className="bg-yellow-50 px-4 py-20 sm:px-6 sm:py-24" data-testid="treats-teaser-section">
    <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
      <Reveal>
        <img src={IMAGES.rainbow.url} alt={IMAGES.rainbow.alt} loading="lazy" className="aspect-[4/3] w-full rounded-3xl object-cover shadow-xl shadow-yellow-500/20" data-testid="treats-teaser-image" />
      </Reveal>
      <Reveal delay={0.1}>
        <span className="mb-4 inline-block rounded-full bg-white px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-yellow-700 shadow-sm">Snowballs & Treats</span>
        <h2 className="text-3xl font-semibold sm:text-4xl lg:text-5xl">Something Sweet Is Waiting</h2>
        <ul className="mt-6 space-y-3">
          {treatsList.map((item) => (
            <li key={item} className="flex items-center gap-3 text-base font-bold text-slate-700">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-yellow-400">
                <Check className="h-4 w-4 text-slate-900" aria-hidden="true" />
              </span>
              {item}
            </li>
          ))}
        </ul>
        <Link
          to="/treats"
          data-testid="treats-teaser-cta"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-blue-500 px-7 py-3.5 text-base font-extrabold text-white shadow-lg shadow-blue-500/30 transition-transform duration-200 hover:-translate-y-0.5 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-300"
        >
          See What We Offer <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const BirthdayTeaser = () => (
  <section className="relative overflow-hidden px-4 py-20 sm:px-6 sm:py-24" data-testid="birthday-teaser-section">
    <Confetti />
    <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
      <Reveal className="order-2 lg:order-1">
        <div className="rounded-3xl border-4 border-dashed border-yellow-400 bg-yellow-50 p-8 text-center shadow-xl shadow-yellow-500/10 sm:p-10" data-testid="birthday-teaser-card">
          <span className="inline-block rounded-full bg-red-500 px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-white">Birthday Party Package</span>
          <p className="mt-5 font-display text-6xl font-bold text-blue-600 sm:text-7xl" data-testid="birthday-teaser-price">$225</p>
          <p className="mt-2 text-base font-extrabold text-slate-700">Up to {SITE.party.maxKids} children · {SITE.party.length}</p>
          <p className="mt-3 text-sm font-semibold text-slate-600">
            Snowballs for every child, party area, Reading Tree, photo area, and a Happy Birthday yard-card display.
          </p>
          <PartyButton testid="birthday-teaser-cta" className="mt-7" />
        </div>
      </Reveal>
      <Reveal delay={0.1} className="order-1 lg:order-2">
        <span className="mb-4 inline-block rounded-full bg-red-100 px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-red-600">Birthday Parties</span>
        <h2 className="text-3xl font-semibold sm:text-4xl lg:text-5xl">Make Their Birthday Extra Sweet!</h2>
        <p className="mt-4 text-base text-slate-600 md:text-lg">
          A birthday celebration filled with snowballs, imagination, pictures, and memories — booked directly
          with Ms. Jeanette herself.
        </p>
        <img src={IMAGES.birthday.url} alt={IMAGES.birthday.alt} loading="lazy" className="mt-8 aspect-[16/9] w-full rounded-3xl object-cover shadow-xl shadow-red-500/10" data-testid="birthday-teaser-image" />
      </Reveal>
    </div>
  </section>
);

const ReadingTeaser = () => (
  <section className="bg-green-50 px-4 py-20 sm:px-6 sm:py-24" data-testid="reading-teaser-section">
    <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
      <Reveal>
        <span className="mb-4 inline-block rounded-full bg-white px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-green-700 shadow-sm">A signature spot</span>
        <h2 className="text-3xl font-semibold sm:text-4xl lg:text-5xl">Discover the Reading Tree</h2>
        <p className="mt-4 inline-block rounded-full bg-green-600 px-4 py-1.5 text-sm font-extrabold text-white" data-testid="reading-teaser-badge">
          Our very own Reading Tree w/ Rewards
        </p>
        <p className="mt-4 text-base text-slate-600 md:text-lg">
          Sit beneath the Reading Tree, discover little doors and whimsical decorations, and let your
          imagination take over.
        </p>
        <p className="mt-3 text-sm font-extrabold text-green-700">
          Read a book beneath the tree for 20 minutes and earn a free small snowball.
        </p>
        <Link
          to="/reading-tree"
          data-testid="reading-teaser-cta"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-green-600 px-7 py-3.5 text-base font-extrabold text-white shadow-lg shadow-green-600/30 transition-transform duration-200 hover:-translate-y-0.5 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-green-300"
        >
          Explore the Reading Tree <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </Link>
      </Reveal>
      <Reveal delay={0.1}>
        <img src={IMAGES.readingTree.url} alt={IMAGES.readingTree.alt} loading="lazy" className="aspect-[4/3] w-full rounded-3xl object-cover shadow-xl shadow-green-600/20" data-testid="reading-teaser-image" />
      </Reveal>
    </div>
  </section>
);

const whyCards = [
  { icon: Heart, title: "Family Friendly", text: "A welcoming place designed around children and families.", bg: "bg-red-100", color: "text-red-500" },
  { icon: Sparkles, title: "Imagination & Fun", text: "Whimsical details give children something to explore and discover.", bg: "bg-yellow-100", color: "text-yellow-600" },
  { icon: Cake, title: "Birthday Memories", text: "A unique setting for a child's special day.", bg: "bg-blue-100", color: "text-blue-600" },
  { icon: BookOpen, title: "Reading & Rewards", text: "The Reading Tree adds an educational and imaginative element.", bg: "bg-green-100", color: "text-green-600" },
  { icon: IceCreamCone, title: "Sweet Treats", text: "Snowballs and specialty treats make every visit sweeter.", bg: "bg-pink-100", color: "text-pink-500" },
  { icon: Camera, title: "Picture-Perfect Moments", text: "A colorful environment designed to create memorable photos.", bg: "bg-blue-100", color: "text-blue-500" },
];

const WhyFamilies = () => (
  <section className="px-4 py-20 sm:px-6 sm:py-24" data-testid="why-families-section">
    <SectionHeading
      eyebrow="Why visit"
      title="Why Families Love Ms. Jeanette's"
      testid="why-families-heading"
    />
    <div className="mx-auto grid max-w-7xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {whyCards.map((card, i) => {
        const Icon = card.icon;
        return (
          <Reveal key={card.title} delay={i * 0.06}>
            <div data-testid={`why-card-${card.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex h-full items-start gap-4 rounded-3xl border border-blue-100 bg-white p-6 shadow-lg shadow-blue-500/10 transition-transform duration-200 hover:-translate-y-1.5">
              <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${card.bg}`}>
                <Icon className={`h-6 w-6 ${card.color}`} aria-hidden="true" />
              </span>
              <div>
                <h3 className="font-display text-lg font-semibold">{card.title}</h3>
                <p className="mt-1 text-sm font-semibold text-slate-600">{card.text}</p>
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const GalleryPreview = () => (
  <section className="bg-blue-50 px-4 py-20 sm:px-6 sm:py-24" data-testid="gallery-preview-section">
    <SectionHeading eyebrow="Photo gallery" title="Sweet Moments" testid="gallery-preview-heading" />
    <div className="mx-auto grid max-w-7xl grid-cols-2 gap-4 lg:grid-cols-4">
      {GALLERY.map((image, i) => (
        <Reveal key={image.caption} delay={i * 0.06}>
          <img src={image.url} alt={image.alt} loading="lazy" data-testid={`gallery-preview-img-${i}`} className="aspect-square w-full rounded-3xl object-cover shadow-lg shadow-blue-500/10 transition-transform duration-200 hover:-translate-y-1 hover:scale-[1.02]" />
        </Reveal>
      ))}
    </div>
    <div className="mt-10 text-center">
      <Link to="/gallery" data-testid="gallery-preview-cta" className="inline-flex items-center gap-2 rounded-full bg-blue-500 px-7 py-3.5 text-base font-extrabold text-white shadow-lg shadow-blue-500/30 transition-transform duration-200 hover:-translate-y-0.5 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-300">
        Visit Ms. Jeanette's Gallery <ArrowRight className="h-5 w-5" aria-hidden="true" />
      </Link>
    </div>
  </section>
);

const FinalCta = () => (
  <section className="relative overflow-hidden bg-blue-600 px-4 py-20 sm:px-6 sm:py-24" data-testid="final-cta-section">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <Star className="absolute left-[8%] top-[20%] h-6 w-6 text-yellow-300/60" />
      <Sparkles className="absolute right-[12%] top-[15%] h-7 w-7 text-white/30" />
      <Star className="absolute bottom-[20%] right-[8%] h-5 w-5 text-yellow-300/50" />
      <Sparkles className="absolute bottom-[25%] left-[15%] h-5 w-5 text-white/25" />
    </div>
    <Reveal className="relative mx-auto max-w-3xl text-center">
      <h2 className="text-3xl font-semibold text-white sm:text-4xl lg:text-5xl">Come for the Snowballs. Stay for the Memories.</h2>
      <p className="mx-auto mt-5 max-w-2xl text-base font-semibold text-blue-100 md:text-lg">
        Whether you're stopping by for a sweet treat, exploring the Reading Tree, or celebrating a special
        birthday, Ms. Jeanette's is here to help make your visit a memory.
      </p>
      <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
        <CallButton testid="final-cta-call-btn" light>{`Call Ms. Jeanette — ${SITE.phone}`}</CallButton>
        <PartyButton testid="final-cta-party-btn" />
      </div>
    </Reveal>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <QuickInfo />
      <MoreThan />
      <TreatsTeaser />
      <BirthdayTeaser />
      <ReadingTeaser />
      <WhyFamilies />
      <GalleryPreview />
      <FinalCta />
    </>
  );
}
